import React, { FC } from 'react';

/** Shared card shell and formatters for the Scaling tab of Platform Stats. */

export const NO_DATA = 'No data yet';

export const fmtPct = (v: number | null) => (v == null ? NO_DATA : `${v}%`);

export const fmtGbp = (v: number | null) =>
  v == null
    ? NO_DATA
    : `£${v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

export const fmtUsd = (v: number | null) =>
  v == null
    ? NO_DATA
    : `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/** Minutes → "45m" / "2h 15m" / "3d 4h", so a long wait stays readable. */
export const fmtMinutes = (v: number | null) => {
  if (v == null) return NO_DATA;
  if (v < 1) return '<1m';
  if (v < 60) return `${Math.round(v)}m`;
  const hours = v / 60;
  if (hours < 48) {
    const h = Math.floor(hours);
    const m = Math.round(v - h * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const days = Math.floor(hours / 24);
  const h = Math.round(hours - days * 24);
  return h > 0 ? `${days}d ${h}h` : `${days}d`;
};

export const fmtHours = (v: number | null) =>
  v == null ? NO_DATA : fmtMinutes(v * 60);

/** ISO date → "d/m" axis label. */
export const fmtDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
};

export const lineOpts = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, grid: { color: 'rgba(148, 163, 184, 0.15)' } },
    x: { grid: { display: false } },
  },
};

export interface MetricCardProps {
  /** Position in the approved order. */
  index: number;
  title: string;
  value: string;
  /** One-line context under the value — kept short to stay scannable. */
  sub?: string;
  trend?: { label: string; direction: 'up' | 'down' | 'flat' };
  /** Definition + cohort sizes + limitations, behind a disclosure. */
  details: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export const MetricCard: FC<MetricCardProps> = ({
  index,
  title,
  value,
  sub,
  trend,
  details,
  children,
  className,
}) => (
  <section className={`scaling-card ${className ?? ''}`}>
    <header className='scaling-card-header'>
      <h3>
        <span className='scaling-card-index'>{index}</span>
        {title}
      </h3>
    </header>
    <div className='scaling-card-value-row'>
      <span className={`scaling-value ${value === NO_DATA ? 'no-data' : ''}`}>
        {value}
      </span>
      {trend && (
        <span className={`scaling-trend ${trend.direction}`}>
          {trend.label}
        </span>
      )}
    </div>
    {sub && <p className='scaling-card-sub'>{sub}</p>}
    {children}
    <details className='scaling-details'>
      <summary>How it&apos;s measured</summary>
      <div className='scaling-details-body'>{details}</div>
    </details>
  </section>
);
